import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';

import { Tab } from '../../shared/tabs.component';
import PeriodDropdown from '../../shared/periodDropdown.component';
import { ReduxAlert } from '../../shared/alert.component';
import {
  CurrencyConversionRatesPropTypes,
  CurrencyConversionRatePropTypes,
  ExtendedCurrencyConversionRatesPropTypes,
  ImportStatsPropTypes,
} from '../../shared/propTypes/currencyConversionRate';
import ViewGrid from '../shared/viewGrid.component';
import columnDefs from '../../shared/columnDefinitions/currencyConversionRates';
import NavigationPrompt, { IMPORTED_DATA_PENDING_MESSAGE } from '../../shared/navigationPrompt.component';

import SecondaryMenu from '../../menu/secondaryMenu.component';

import { PeriodPropTypes } from '../../shared/propTypes/period';
import { withPermissions, permissions } from '../../shared/authorization';

import { modalDataSelector } from '../../shared/modals/modals.selectors';
import ModalNames from './modalNames';
import {
  fetchCurrencyConversionRates,
  uploadCurrencyConversionRatesFromFile,
  importCurrencyConversionRates,
  clearUploadedRates,
} from './store/actions';
import AddCurrencyConversionRateModal from './addCurrencyConversionRateModal.component';

import exportToExcel from './exportToExcel';
import exportMissingRatesToExcel from './exportMissingRatesToExcel';

import { displayZeroSelector, hideDecimalsSelector, negativeMinusSelector } from '../../userData/store/selectors';
import { fromSearchString } from '../../shared/urlUtils';
import {
  periodOrCurrentSelector,
  environmentSuffixSelector,
  currentPeriodSelector,
} from '../../shared/store/selectors';
import {
  currencyConversionRatesSelector,
  fetchingCurrencyConversionRatesSelector,
  extendedUploadedCurrencyConversionRatesSelector,
  uploadingCurrencyConversionRatesSelector,
  importingCurrencyConversionRatesSelector,
  areImportsPendingSelector,
  missingRatesSelector,
  importingCurrencyConversionRatesProgress,
  importStatsSelector,
} from './store/selectors';

import CurrencyConversionRatesImport from './currencyConversionRatesImport.component';
import CurrencyConversionRatesMissingRates from './currencyConversionRatesMissingRates.component';
import CurrencyConversionRatesAdminColumnRenderer from './currencyConversionRatesAdminColumnRenderer.component';
import HamburgerDropdown from './hamburgerDropdown.component';
import RemoveCurrencyConversionRateModal from './removeCurrencyConversionRateModal.component';
import { mergeColumnDefinitionsField } from '../../utils/columnDefinitionUtils';
import { sortBySeverity } from '../../shared/sortBySeverity';
import { ViewImportTabs } from '../shared/ViewImportTabs.component';

const TabTypes = {
  VIEW: 'VIEW',
  IMPORT: 'IMPORT',
  MISSING_RATES: 'MISSING_RATES',
};

const columnDefinitionsWithDefaultSorting = mergeColumnDefinitionsField(['severity'], {
  sort: 'asc',
  comparator: (a, b) => sortBySeverity({ severity: a }, { severity: b }),
})(columnDefs);

class CurrencyConversionRatesPage extends PureComponent {
  static propTypes = {
    addEditModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      data: CurrencyConversionRatePropTypes,
    }),
    removeModalData: PropTypes.shape({
      show: PropTypes.bool.isRequired,
      data: PropTypes.shape({
        entity: CurrencyConversionRatePropTypes.isRequired,
        rowIndex: PropTypes.number.isRequired,
      }),
    }),
    period: PeriodPropTypes.isRequired,
    currentPeriod: PeriodPropTypes.isRequired,
    currencyConversionRates: CurrencyConversionRatesPropTypes,
    uploadedCurrencyConversionRates: CurrencyConversionRatesPropTypes,
    extendedUploadedCurrencyConversionRates: ExtendedCurrencyConversionRatesPropTypes,
    isFetchingCurrencyConversionRates: PropTypes.bool.isRequired,
    fetchCurrencyConversionRates: PropTypes.func.isRequired,
    uploadCurrencyConversionRatesFromFile: PropTypes.func.isRequired,
    uploadingCurrencyConversionRates: PropTypes.bool.isRequired,
    importCurrencyConversionRates: PropTypes.func.isRequired,
    importingCurrencyConversionRates: PropTypes.bool.isRequired,
    importingCurrencyConversionRatesProgress: PropTypes.number.isRequired,
    areImportsPending: PropTypes.bool.isRequired,
    missingRates: CurrencyConversionRatesPropTypes,
    shouldDisplayZero: PropTypes.bool.isRequired,
    hideDecimals: PropTypes.bool.isRequired,
    negativeMinus: PropTypes.bool.isRequired,
    environmentSuffix: PropTypes.string.isRequired,
    canModifyInSelectedPeriod: PropTypes.bool.isRequired,
    importStats: ImportStatsPropTypes,
    clearUploadedRates: PropTypes.func.isRequired,
    location: PropTypes.shape({
      search: PropTypes.string,
    }),
  };

  constructor(props) {
    super(props);
    this.state = {
      activeTab: TabTypes.VIEW,
    };
  }

  state = {
    isExportingMissingRates: false,
  };

  componentDidMount() {
    const { period, fetchCurrencyConversionRates } = this.props;
    fetchCurrencyConversionRates(period);
  }

  componentDidUpdate(prevProps) {
    const {
      importingCurrencyConversionRates,
      period,
      fetchCurrencyConversionRates,
      missingRates,
    } = this.props;

    const shouldFetchData = prevProps.importingCurrencyConversionRates && !importingCurrencyConversionRates;
    if (shouldFetchData) {
      this.changeTab(TabTypes.VIEW);
      fetchCurrencyConversionRates(period);
    }
    const shouldFocusMissingRates = prevProps.missingRates.length === 0 && missingRates.length > 0;
    if (shouldFocusMissingRates) {
      this.changeTab(TabTypes.MISSING_RATES);
    }
  }

  componentWillUnmount() {
    this.props.clearUploadedRates();
  }

  oldSearch = null;
  oldInitialFilters = {};

  exportToExcel = callback => {
    const {
      currencyConversionRates,
      period,
      environmentSuffix,
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
    } = this.props;

    exportToExcel({
      currencyConversionRates,
      periodName: period.name,
      environmentSuffix,
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
    });
    callback();
  };

  exportMissingRatesToExcel = callback => {
    const {
      missingRates,
      period,
      environmentSuffix,
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
    } = this.props;

    exportMissingRatesToExcel({
      missingRates,
      periodName: period.name,
      environmentSuffix,
      shouldDisplayZero,
      hideDecimals,
      negativeMinus,
    });
    callback();
  };

  selectPeriod = selectedPeriod => {
    const { fetchCurrencyConversionRates, currentPeriod } = this.props;

    fetchCurrencyConversionRates(selectedPeriod);

    if (selectedPeriod.name !== currentPeriod.name) {
      this.setState({ activeTab: TabTypes.VIEW });
    }
  };

  changeTab = activeTab => this.setState({ activeTab });

  uploadCurrencyConversionRates = file => {
    const { uploadCurrencyConversionRatesFromFile, currentPeriod } = this.props;
    uploadCurrencyConversionRatesFromFile({ file, currentPeriod });
  };

  fileToImportChange = event => {
    this.uploadCurrencyConversionRates(event.target.files[0]);
  };

  fileToImportSelect = hideDropDown => () => {
    this.setState({ activeTab: TabTypes.IMPORT },
      () => {
        hideDropDown();
      });
  };

  generateTemplateFileName = () => `TLS_Currency_Conversion_Rates_${this.props.period.name}${this.props.environmentSuffix}`;

  getInitialFilters = () => {
    const search = this.props.location.search;
    if (search === this.oldSearch) {
      return this.oldInitialFilters;
    }
    this.oldSearch = search;
    this.oldInitialFilters = {
      fromCurrency: fromSearchString(search, true)?.fromCurrency,
      toCurrency: fromSearchString(search, true)?.toCurrency,
    };

    return this.oldInitialFilters;
  };

  getAdditionalTabs = () => {
    const {
      isFetchingCurrencyConversionRates,
      missingRates,
    } = this.props;

    return [
      <Tab value={TabTypes.MISSING_RATES} key={TabTypes.MISSING_RATES} label="Missing Rates">
        <CurrencyConversionRatesMissingRates
          missingRates={missingRates}
          isFetchingCurrencyConversionRates={isFetchingCurrencyConversionRates}
        />
      </Tab>,
    ];
  };

  renderViewTab = () => {
    const {
      canModifyInSelectedPeriod,
      currencyConversionRates,
      isFetchingCurrencyConversionRates,
    } = this.props;

    const initialFilters = this.getInitialFilters();

    return (
      <ViewGrid
        adminColumnRenderer={CurrencyConversionRatesAdminColumnRenderer}
        className="admin-import-grid"
        columnDefs={columnDefinitionsWithDefaultSorting}
        data={currencyConversionRates}
        isLoading={isFetchingCurrencyConversionRates}
        shouldDisplayAdminColumn={canModifyInSelectedPeriod}
        initialFilters={initialFilters}
      />
    );
  };

  renderImportTab = () => {
    const {
      currencyConversionRates,
      isFetchingCurrencyConversionRates,
      extendedUploadedCurrencyConversionRates,
      uploadingCurrencyConversionRates,
      importingCurrencyConversionRates,
      importingCurrencyConversionRatesProgress,
      importCurrencyConversionRates,
      period,
      importStats,
    } = this.props;

    return (
      <CurrencyConversionRatesImport
        currencyConversionRates={currencyConversionRates}
        extendedUploadedCurrencyConversionRates={extendedUploadedCurrencyConversionRates}
        uploadCurrencyConversionRates={this.uploadCurrencyConversionRates}
        isFetchingCurrencyConversionRates={isFetchingCurrencyConversionRates}
        uploadingCurrencyConversionRates={uploadingCurrencyConversionRates}
        importingCurrencyConversionRates={importingCurrencyConversionRates}
        importingCurrencyConversionRatesProgress={importingCurrencyConversionRatesProgress}
        period={period}
        importCurrencyConversionRates={importCurrencyConversionRates}
        templateFileName={this.generateTemplateFileName()}
        importStats={importStats}
      />
    );
  };

  render() {
    const {
      activeTab,
    } = this.state;
    const {
      period,
      addEditModalData,
      removeModalData,
      canModifyInSelectedPeriod,
      areImportsPending,
      missingRates,
      isFetchingCurrencyConversionRates,
    } = this.props;

    return (
      <div className="admin-import-page">
        <NavigationPrompt
          show={areImportsPending}
          message={<span>{IMPORTED_DATA_PENDING_MESSAGE}</span>}
        />
        <SecondaryMenu>
          <PeriodDropdown
            onChange={this.selectPeriod}
          />
          <div className="hamburger-dropdown ml-auto text-right">
            <HamburgerDropdown
              canModifyInSelectedPeriod={canModifyInSelectedPeriod}
              missingRates={missingRates}
              period={period}
              fileToImportChange={this.fileToImportChange}
              fileToImportSelect={this.fileToImportSelect}
              exportToExcel={this.exportToExcel}
              exportMissingRatesToExcel={this.exportMissingRatesToExcel}
              templateFileName={this.generateTemplateFileName()}
              isFetchingCurrencyConversionRates={isFetchingCurrencyConversionRates}
            />
          </div>
        </SecondaryMenu>
        <div className="page">
          <div className="container-fluid">
            <ReduxAlert/>
            <div className="row no-gutters page-content-container">
              <div className="col">
                <div className="d-flex flex-row justify-content-between">
                  <h5>Currency Conversion Rates</h5>
                </div>
              </div>
            </div>
            <div className="row no-gutters page-content-container">
              <div className="col">
                <ViewImportTabs
                  canModify={canModifyInSelectedPeriod}
                  activeTab={activeTab}
                  changeTab={this.changeTab}
                  renderViewTab={this.renderViewTab} // eslint-disable-line react/jsx-no-bind
                  renderImportTab={this.renderImportTab} // eslint-disable-line react/jsx-no-bind
                  viewTabOnly={!canModifyInSelectedPeriod}
                  additionalTabs={this.getAdditionalTabs()}
                />
              </div>
            </div>
          </div>
        </div>
        <AddCurrencyConversionRateModal
          entityToEdit={addEditModalData.data}
          period={period}
          isVisible={addEditModalData && addEditModalData.show}
          openForEdit={Boolean(addEditModalData && addEditModalData.data)}
        />
        <RemoveCurrencyConversionRateModal
          entityToRemove={removeModalData.data && removeModalData.data.entity}
          rowIndex={removeModalData.data && removeModalData.data.rowIndex}
          isVisible={removeModalData.show}
        />
        <ReactTooltip place="right" effect="float" />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    addEditModalData: modalDataSelector(state, ModalNames.CurrencyConversionRateAddEdit),
    removeModalData: modalDataSelector(state, ModalNames.CurrencyConversionRateRemove),
    period: periodOrCurrentSelector(state),
    currentPeriod: currentPeriodSelector(state),
    environmentSuffix: environmentSuffixSelector(state),
    currencyConversionRates: currencyConversionRatesSelector(state),
    isFetchingCurrencyConversionRates: fetchingCurrencyConversionRatesSelector(state),
    shouldDisplayZero: displayZeroSelector(state),
    hideDecimals: hideDecimalsSelector(state),
    negativeMinus: negativeMinusSelector(state),
    extendedUploadedCurrencyConversionRates: extendedUploadedCurrencyConversionRatesSelector(state),
    uploadingCurrencyConversionRates: uploadingCurrencyConversionRatesSelector(state),
    importingCurrencyConversionRates: importingCurrencyConversionRatesSelector(state),
    importingCurrencyConversionRatesProgress: importingCurrencyConversionRatesProgress(state),
    areImportsPending: areImportsPendingSelector(state),
    missingRates: missingRatesSelector(state),
    importStats: importStatsSelector(state),
  };
}

export default withPermissions(permissions.CURRENCY_CONVERSION_RATES)(connect(
  mapStateToProps,
  {
    fetchCurrencyConversionRates,
    uploadCurrencyConversionRatesFromFile,
    importCurrencyConversionRates,
    clearUploadedRates,
  }
)(CurrencyConversionRatesPage));
